import {
  formatDateTimeForGui,
  formatOnlyDateForGui,
  toYearMonthDay,
} from "../components/pages/utils";

import { Consts } from "../components/pages/consts";

const formatDateForLabsAndMeasurements = (dateStr) => {
  const date = new Date(dateStr);

  return date?.getFullYear() + '-' +
    String(date?.getMonth() + 1)?.padStart(2, '0') + '-' +
    String(date?.getDate())?.padStart(2, '0') + ' ' +
    String(date?.getHours())?.padStart(2, '0') + ':' +
    String(date?.getMinutes())?.padStart(2, '0') + ':' +
    String(date?.getSeconds())?.padStart(2, '0');
}

export const transformLabTestToSend = (labTest) => {
  const params = labTest?.labParams?.map((param) => {
    return {
      parameter_name: param?.parameterName,
      value: param?.value,
      unit: param?.unit,
    };
  });

  const formattedDate = formatDateForLabsAndMeasurements(labTest?.testDate);

  return {
    lab_test_name: labTest?.labTestName,
    specimen_type: Consts.ANY?.toUpperCase(),
    comment: Consts.ANY?.toUpperCase(),
    test_date: formattedDate,
    labparameterlist: params,
  };
};

const buildLabTestParams = (params) => {
  return params?.map((param) => {
    return {
      parameterName: param?.parameter_name,
      value: param?.value,
      unit: param?.unit,
    };
  });
};

export const transformReceivedLabTests = (receivedResult) => {
  return receivedResult?.lab_tests?.map((labTest) => {
    return {
      id: labTest?.id,
      labTestName: labTest?.lab_test_name,
      testDate: formatDateTimeForGui(labTest?.test_date),
      labParams: buildLabTestParams(labTest?.labparameterlist),
    };
  });
};

export const transformDrugToUpdate = (mmcDrug) => {
  if (mmcDrug === null) {
    return null;
  }

  return {
    drug: {
      product_name: mmcDrug?.productName,
      atc: mmcDrug?.atc,
      strength: mmcDrug?.strength,
      strength_unit: mmcDrug?.strengthUnit,
      active_substance: mmcDrug?.active_substance,
    },
    prescription: {
      id: mmcDrug?.id,
      start_date: mmcDrug?.startDate,
      frequency: +mmcDrug?.frequency,
      frequency_unit: mmcDrug?.frequencyUnit,
      intake_type: mmcDrug?.intakeType,
      timing_type: mmcDrug?.timingType,
      comment: "",
      status: mmcDrug?.status,
    },
    intakes: mmcDrug?.intakes?.map((intake) => {
      return {
        intake_time: intake?.intakeTime,
        dosage: +intake?.dosage,
        dosage_unit: intake?.dosageUnit,
      };
    }),
  };
};

const buildIntakes = (intakes) => {
  return intakes?.map((intake) => {
    return {
      intake_time: intake?.intakeTime
        ? intake?.intakeTime
        : Consts.DEFAULT_DAY_TIMES[0], // 8:00
      dosage: intake?.dosage ? +intake?.dosage : -1,
      dosage_unit: intake?.dosageUnit?.dosageUnit ? intake?.dosageUnit?.dosageUnit : Consts.OTHER,
    };
  });
}

export const transformNewDrugToSend = (mmcDrug) => {
  if (mmcDrug === null) {
    return null;
  }

  const product = mmcDrug?.productName ? mmcDrug?.productName : Consts.ANY;

  const substance = mmcDrug?.activeSubstance && typeof mmcDrug?.activeSubstance === "object"// TODO: if we change the input value from  [] to string, we need only replace , with &&& if needed
    ? mmcDrug?.activeSubstance?.join('&&&')
    : Consts.ANY

  const abda = mmcDrug?.abda && typeof mmcDrug?.abda === "object"
    ? mmcDrug?.abda?.join('&&&')
    : Consts.ANY

  const intakes = buildIntakes(mmcDrug?.intakes);

  if(substance === Consts.ANY && product === Consts.ANY){
    return null;
  }

  let strengthUnit;

  if(typeof mmcDrug?.strengthUnit === "string"){
    strengthUnit = mmcDrug?.strengthUnit ? mmcDrug?.strengthUnit : Consts.OTHER;
  } else {
    strengthUnit = mmcDrug?.strengthUnit?.strengthUnit ? mmcDrug?.strengthUnit?.strengthUnit : Consts.OTHER;
  }

  return {
    drug: {
      product_name: product,
      active_substance: substance, // it should be a string with '&&&' as delimiter between substances
      atc: mmcDrug?.atc ? mmcDrug?.atc : "",
      name_national_code: !mmcDrug?.code || mmcDrug?.code === "" ? Consts.ANY : Consts.PZN,
      value_national_code: !mmcDrug?.code || mmcDrug?.code === "" ? Consts.ANY : mmcDrug?.code,
      strength: mmcDrug?.strength ? mmcDrug?.strength : -1,
      strength_unit: strengthUnit,
    },
    prescription: {
      start_date: mmcDrug?.startDate
        ? mmcDrug?.startDate
        : Consts.DEFAULT_DRUG_START,
      frequency: +mmcDrug?.frequency ? mmcDrug?.frequency : -1,
      frequency_unit: mmcDrug?.frequencyUnit
        ? mmcDrug?.frequencyUnit
        : Consts.OTHER,
      intake_type: mmcDrug?.intakeType ? mmcDrug?.intakeType : Consts.REGULARLY,
      timing_type: mmcDrug?.timingType ? mmcDrug?.timingType : Consts.DAY_TIMES,
      comment: abda, // we store the substances in abda to the comment field in the DB
      status: mmcDrug?.status ? mmcDrug?.status : Consts.ACTIVE,
    },
    intakes: intakes,
  };
};

export const transformGeneTestToSend = (geneTest) => {
  if (geneTest === null) {
    return null;
  }

  return {
    test_date: geneTest?.testDate,
    lab_name: geneTest?.labName,
    genes: geneTest?.genes?.map((gene) => {
      const genoRisks = gene?.genoRisks?.map((risk) => {
        return {
          active_substance: risk?.substanceKey,
          recommendation: risk?.recommendation,
          literature: risk?.description,
        };
      });

      const phenoRisks = gene?.phenoRisks?.map((risk) => {
        return {
          active_substance: risk?.substanceKey,
          recommendation: risk?.recommendation,
          literature: risk?.description,
        };
      });

      const completeRisks = gene?.risks?.map((risk) => {
        return {
          active_substance: risk?.substanceKey,
          recommendation: risk?.recommendation,
          literature: risk?.description,
        };
      });

      let risks = [];

      if (genoRisks?.length > 0) {
        risks = [...risks, ...genoRisks];
      }

      if (phenoRisks?.length > 0) {
        risks = [...risks, ...phenoRisks];
      }

      if (completeRisks?.length > 0) {
        risks = [...risks, ...completeRisks];
      }

      return {
        gene_name: gene?.geneName,
        genotype: gene?.genotype === "*/*" ? "*1/*1" : gene?.genotype,
        phenotype: gene?.phenotype,
        risks: risks,
      };
    }),
  };
};

export const transformReceivedDrugs = (receivedDrugs, risks, intakes) => {
  return receivedDrugs?.prescriptions?.map((drug) => {
    let ddis = risks ? drug?.ddis : null;
    let dgis = risks ? drug?.dgis : null;
    let ddgi = risks ? drug?.ddgi : null;
    let drugIntakes = intakes ? transformReceivedIntakes(drug?.intakes) : null;

    if (ddis?.length === 1 && !ddis[0]?.relevance) {
      // in this case ddi is: [{comment: '10'}] - TODO solve it in the backend
      ddis = [];
    }

    const unit = drug?.strength_unit === "" || typeof drug?.strength_unit === "string"
      ? drug?.strength_unit
      : JSON.parse(drug?.strength_unit);

    return {
      id: drug?.id,
      productName: drug?.product_name,
      activeSubstance: drug?.active_substance,
      atc: drug?.atc,
      nameNationalCode: drug?.name_national_code,
      valueNationalCode: drug?.value_national_code,
      strength: drug?.strength,
      strengthUnit: unit,
      startDate: formatOnlyDateForGui(drug?.start_date),
      frequency: drug?.frequency,
      frequencyUnit: drug?.frequency_unit,
      intakeType: drug?.intake_type,
      timingType: drug?.timing_type,
      status: drug?.status,
      dgis: dgis,
      ddgi: ddgi,
      ddis: ddis,
      intakes: drugIntakes,
    };
  });
};

export const transformReceivedIntakes = (intakes) => {
  return intakes?.map((intake) => {
    return {
      id: intake?.id,
      prescriptionId: intake?.prescription_id,
      intakeTime: intake?.intake_time,
      dosage: intake?.dosage,
      dosageUnit: intake?.dosage_unit,
    };
  });
};

const buildGene = (gene) => {
  const genoRisks = [];
  const phenoRisks = [];
  const risks = [];

  gene?.risks?.forEach((risk) => {
    const riskObj = {
      substanceKey: risk?.active_substance,
      id: risk?.id,
      description: risk?.literature,
      recommendation: risk?.recommendation,
    };

    if (risk?.literature === Consts.GENO_RISKS) {
      genoRisks?.push(riskObj);
    } else if (risk?.literature === Consts.PHENO_RISKS) {
      phenoRisks?.push(riskObj);
    } else if (risk?.literature === Consts.COMPLETE_RISKS) {
      risks?.push(riskObj);
    }
  });

  return {
    id: gene?.id,
    geneName: gene?.gene_name,
    genotype: gene?.genotype,
    phenotype: gene?.phenotype,
    phenoRisks,
    genoRisks,
    risks,
  };
};

export const transformReceivedPgx = (pgxResult) => {
  if (!pgxResult) {
    return null;
  }

  if (pgxResult?.gene_test) {
    const genes = [];

    pgxResult?.gene_test?.genes?.forEach((gene) => {
      genes?.push(buildGene(gene));
    });

    return {
      id: pgxResult?.gene_test?.id,
      labName: pgxResult?.gene_test?.lab_name,
      testDate: pgxResult?.gene_test?.test_date?.split("T")[0],
      genes: genes,
    };
  }

  return null;
};

export const transformReceivedGenes = (genes) => {
  if (!genes) {
    return null;
  }

  return genes?.genes?.map((gene) => {
    return {
      id: gene?.id,
      geneName: gene?.gene_name,
      genotype: gene?.genotype,
      phenotype: gene?.phenotype,
      labName: gene?.lab_name,
      risks: gene?.risks,
      testDate: formatDateTimeForGui(gene?.test_date),
      testId: gene?.test_id
    };
  });
};

export const transformDiseaseToSend = (disease) => {
  return {
    name: disease?.icd10name,
    icd10: disease?.icd10,
    icd10name: disease?.icd10name,
    snomed: disease?.snomed ? disease?.snomed : "",
    type: disease?.category,
    begin: disease?.begin
  };
};

export const transformNewSymptomsToSend = (symptoms, patientId) => {
  return symptoms?.map((symptom) => {
    return {
      type: symptom?.type,
      term: symptom?.term,
      outbreak_date: toYearMonthDay(symptom?.startDate),
      suffering_level: symptom?.sufferingLevel,
      feeling_now: symptom?.sufferingLevel,
      sending_date: toYearMonthDay(symptom?.endDate ? symptom?.endDate : new Date()),
      patient_id: patientId,
      comment: symptom?.comment,
      reported: false,
      deactivated: symptom?.endDate ? 1 : 0,
      files: symptom?.files,
    };
  });
};

export const transformUpdatedSymptomToSend = (symptom) => {
  return {
    outbreak_date: toYearMonthDay(symptom?.startDate),
    suffering_level: symptom?.sufferingLevel,
    sending_date: toYearMonthDay(symptom?.endDate ? symptom?.endDate : new Date()),
    comment: symptom?.comment,
    deactivated: symptom?.endDate ? 1 : 0,
    id: symptom.id
  };
};

export const transformReceivedSymptoms = (result) => {
  return result?.map((symptom) => {
    return {
      id: symptom?.id,
      patientId: symptom?.patient_id,
      type: symptom?.type,
      term: symptom?.term,
      sufferingLevel: symptom?.suffering_level,
      comment: symptom?.comment,
      startDate: toYearMonthDay(symptom.outbreak_date),
      endDate: symptom?.deactivated === 0 ? "" : toYearMonthDay(symptom?.sending_date),
      deactivated: symptom?.deactivated,
      images: symptom?.images ? symptom?.images : []
    };
  });
};

export const transformNewReceivedSymptoms = (symptoms, idsObj) => {
  if (idsObj?.length > 0) {
    const ids = idsObj?.map((item) => item?.symptom_id);
    const transformedSymptoms = [];

    symptoms?.forEach((symptom, index) => {
      if (ids[index]) {
        transformedSymptoms.push({
          id: ids[index],
          patientId: symptom?.patient_id,
          type: symptom?.type,
          term: symptom?.term,
          sufferingLevel: symptom?.suffering_level,
          comment: symptom?.comment,
          startDate: toYearMonthDay(symptom?.outbreak_date),
          endDate: toYearMonthDay(symptom?.sending_date),
          deactivated: symptom?.deactivated,
        })
      }
    });

    return transformedSymptoms;
  }

  return null;
}

export const transformReceivedMeasurements = (result) => {
  return result?.measurements?.map((measurement) => {
    return {
      comment: measurement?.comment,
      id: measurement?.id,
      dateTime: formatDateTimeForGui(measurement?.measurement_date_time),
      params: measurement?.measurementvalueList,
      name: measurement?.name,
      patientId: measurement?.patient_id,
    };
  });
};

const buildMeasurementParamsToSend = (params) => {
  return params?.map((param) => {
    return {
      name: param?.name,
      value: param?.value,
      unit: param?.unit,
    };
  });
};

export const transformMeasurementToSend = (measurement) => {
  const params = buildMeasurementParamsToSend(measurement?.params);
  const formattedDate = formatDateForLabsAndMeasurements(measurement?.dateTime);

  return {
    name: measurement?.name,
    measurement_date_time: formattedDate,
    comment: Consts.ANY,
    value_list: params,
  };
};

export const transformPatientToUpdate = (patient) => {
  const drinkingFrequency =
    patient?.drinking_habit === null || patient?.drinking_habit === Consts.NEVER ? null : patient?.drinking_frequency;

  const smokingHabit = patient?.smoking_habit === "occasionally_regularly"
    ? "occasionally"
    : patient?.smoking_habit;

  return {
    gender: patient?.gender ? patient?.gender : null,
    ethnicity: patient?.ethnicity ? patient?.ethnicity : "mixed_background",
    education: patient?.education ? patient?.education : null,
    smoking_habit: smokingHabit ? smokingHabit : null,
    drinking_habit: patient?.drinking_habit ? patient?.drinking_habit : null,
    drinking_frequency: drinkingFrequency,
    registration_date: patient?.registration_date ? patient?.registration_date?.substring(0, 10) : null,
    id: patient?.id,
  }
};

export const transformReceivedUser = (receivedObj) => {
  if(Array.isArray(receivedObj)){
    return receivedObj;
  } else {
    const smokingHabit = receivedObj?.smoking_habit === "occasionally"
      ? "occasionally_regularly"
      : receivedObj?.smoking_habit;

    return {...receivedObj, smoking_habit: smokingHabit};
  }
};
